import { observable, action } from 'mobx'
import { Theme } from '@zn-material-ui'
import Store from 'store'
import themes from '@themes'
import { getUrlParam } from '@utils'
import CommonApi, {
  IHomeDetail,
  PcVersionEnum,
  IHome,
} from '@services/api/Common'
import Login, { IIndividualLogin, IIndividualInfo } from '@services/api/Login'
import UserStore from './UserStore'
import IntlCommon from '@stores/IntlCommon'

export type ThemeType = {
  id: string
  styleTheme: Theme
  components: { [x: string]: any }
  coverImg: string
  logo: string
  platformName: string
  title: string
  views?: { [x: string]: any }
  [x: string]: any
}

interface ITheme {
  isPcOpen: boolean
  platformName: string
  individuationKey: string
  logoUrl: string
  primary: string
}

export interface IAppStore {
  /** 当前主题 */
  theme: ThemeType
  /** 是否开启首页配置 */
  isPcOpen: boolean
  /** 是否开启个性化登录 */
  isCustomLogin: boolean
  /** 个性化登录路径 */
  loginUrl: string
  getCustomLoginInfo: () => void
  setTheme: (pageId: string, isOpenPcVersion: number) => void
  setCustomLogin: (platformName: string, coverImg: string) => void
  setIcon: (favicon: string) => void
  setHomeList: (homeList: IHomeDetail[]) => void
  isHorizontal: (pageId: string) => boolean
  getHomeList: () => Promise<any>
  /** 个性化登录id */
  individuationKey: string
  /** 个性化登录信息 */
  individualInfo: IIndividualInfo
  /** 首页列表 */
  homeList: IHomeDetail[]
  /** 当前首页ID， 统一在UserStore中维护 */
  // homeId: string
  /** 一账通登录地址 */
  oneLoginUrl: string
  /** 获取一账通登录地址 */
  fetchLoginUrl: () => void
  /** 是否启用长页配置 */
  isLongPage: boolean
  /** 验签用时间间隔 */
  timeGap: number
  setTimeGap: (timeGap: number) => Promise<any>
}

class AppStore implements IAppStore {
  @observable public theme: ThemeType = themes.defaultTheme
  @observable public isPcOpen = false
  @observable public isCustomLogin = false
  @observable public isLongPage = false
  @observable public individuationKey = ''
  @observable public loginUrl = ''
  @observable public homeList = [] as IHomeDetail[]
  @observable public oneLoginUrl = ''
  @observable public individualInfo = {} as IIndividualInfo
  public timeGap = 0

  constructor() {
    const isOpenPcVersion = (Store.get('isPcOpen') && 1) || 0

    if (Store.get('isSingleLogin') && location.hash.includes('#/login')) {
      return
    }

    const pageId = Store.get('homeId') || ''
    this.setTheme(pageId, isOpenPcVersion)
  }

  public initStore = async (pageId: string, theme: ITheme, timeGap: number) => {
    this.setTheme(
      pageId,
      theme.isPcOpen ? PcVersionEnum.true : PcVersionEnum.false
    )
    await this.setTimeGap(timeGap)
    Store.set('theme', theme)
  }

  @action
  public setHomeList = (homeList: IHomeDetail[]) => (this.homeList = homeList)

  @action
  public setTheme = (pageId: string, isOpenPcVersion: number) => {
    UserStore.setHomeId(pageId)

    const isPcOpen = isOpenPcVersion === PcVersionEnum.true
    if (isPcOpen === this.isPcOpen) {
      if (!Store.get('isPcOpen')) {
        Store.set('isPcOpen', isPcOpen)
      }
      return
    }
    const coverImg = this.theme.coverImg
    const platformName = this.theme.platformName
    this.theme = isPcOpen ? themes.horizontalTheme : themes.defaultTheme
    this.theme.coverImg = coverImg
    this.theme.platformName = platformName
    this.isPcOpen = isPcOpen
    Store.set('isPcOpen', isPcOpen)
    if (!Store.get('isCustomLogin')) {
      // 切换网页名
      if (IntlCommon.browserLanguage === 'zh') {
        document.title = this.theme.title
      } else if (IntlCommon.browserLanguage === 'tw') {
        document.title = '學習平臺'
      } else if (IntlCommon.browserLanguage === 'en') {
        document.title = 'Learning platform'
      }
    }
  }

  @action
  public getCustomLoginInfo = async () => {
    const searchKey = getUrlParam('individuationKey')
    const currentPath = location.hash.split('?') || []
    const defaultHash = currentPath[0] === '#/' || currentPath[0] === '#/login'
    const customDomain = window.location.host !== 'www.zhi-niao.com' // 个性化域名，ng自动跳转的个性化域名没带individuationKey
    if (!searchKey && defaultHash && !customDomain) {
      this.individualInfo = {} as IIndividualInfo
      this.loginUrl = ''
      this.individuationKey = ''
      this.isCustomLogin = false
      this.theme.coverImg = themes.defaultTheme.coverImg
      this.theme.platformName = themes.defaultTheme.platformName
      Store.set('individuationKey', '')
      Store.set('isCustomLogin', false)
      return
    }
    const individuationKey = searchKey || Store.get('individuationKey')
    if ((individuationKey && individuationKey !== this.individuationKey) || customDomain){
      const res = await Login.getIndividuationLoginInfoByKey(individuationKey)
      const { body, code } = res as IIndividualLogin
      if (code !== 0 || !body) {
        this.individualInfo = {} as IIndividualInfo
        this.loginUrl = ''
        this.individuationKey = individuationKey
        this.theme.coverImg = themes.defaultTheme.coverImg
        this.theme.platformName = themes.defaultTheme.platformName
        Store.set('individuationKey', individuationKey)
        return
      }

      const { facadeUrl, loginUrl, platformName, ...rest } = body

      this.loginUrl = loginUrl
      this.individuationKey = individuationKey
      this.isLongPage = rest.isUsingLongPage
      this.individualInfo = body
      this.setCustomLogin(platformName, facadeUrl)
      UserStore.setHomeId(rest.pageId)
      Store.set('homeId', rest.pageId)
      Store.set('individuationKey', individuationKey)
    }
  }
  @action
  public fetchLoginUrl = async () => {
    if (!this.oneLoginUrl) {
      const result = await Login.getYZTLgoinUrl()
      if (result.body && result.body.loginUrl) {
        this.oneLoginUrl = result.body.loginUrl
      } else {
        global.$message(result.message)
      }
    }
  }

  @action
  public setCustomLogin = async (platformName: string, coverImg: string) => {
    this.isCustomLogin = true
    this.theme.platformName = platformName || themes.defaultTheme.platformName
    this.theme.coverImg = coverImg || themes.defaultTheme.coverImg
    this.setIcon('')

    Store.set('isCustomLogin', true)
    document.title = platformName || themes.defaultTheme.title
  }

  // 判断是否是交警网校
  public isHorizontal = (pageId: string) => {
    const home = this.homeList.find(v => v.pageId === pageId)
    let isPcOpen = false

    if (home) {
      isPcOpen = home.isOpenPcVersion === PcVersionEnum.true
    }
    this.isPcOpen = isPcOpen
    return isPcOpen
  }
  @action
  public getHomeList = async () => {
    const res = (await CommonApi.getHomeList()) as IHome
    if (!res || !res.body || !res.body.length || res.code !== 0) {
      return Promise.resolve(true)
    }
    const body = res.body
    const homeId = Store.get('homeId')
    this.homeList = body
    const home = body.find(v => v.pageId === homeId) || body[0]
    if (home) {
      this.setTheme(home.pageId, home.isOpenPcVersion)
    }
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 0)
    })
  }

  // 设置favicon
  @action
  public setIcon = (favicon: string) => {
    const link = document.querySelector(
      "link[rel*='icon']"
    ) as HTMLLinkElement | null

    if (link) {
      if (favicon) {
        link.type = 'image/x-icon'
        link.rel = 'shortcut icon'
        link.href = favicon
      } else {
        link.style.display = 'none'
      }
    }
  }

  public setTimeGap = async (timeGap: number) => {
    return new Promise(resolve => {
      this.timeGap = timeGap
      Store.set('timeGap', timeGap)
      setTimeout(() => resolve(true), 0)
    })
  }
}

export default new AppStore()
